import React from "react"

import { Col, Container, OverlayTrigger, Popover, Row } from "react-bootstrap"

import OrderResultRecord from "../../api/OrderResults/OrderResultModel/OrderResultRecord"
import Stage, { Collect, StatusDetail } from "../../api/OrderResults/OrderResultModel/Stage"
import { Status } from "../../api/OrderResults/OrderResultModel/Status"
import ImageOrderExpired from "../../assets/images/order-expired.png"
import { FormatShortDateTime } from "../../formatters/FormatDate"

const stepStatusColor = (stage: Stage, showCompleteOrders: boolean) => {
  // TODO: code below was ported from angular application, refactor into meaningful class names (e.g. success, warning error etc)
  switch (stage.Status) {
    case Status.None:
      return "status-none"
    case Status.Completed:
      return showCompleteOrders ? "status-completed-close" : "status-completed"
    case Status.InProcess:
      return "status-in-progress"
    case Status.Alert:
      return "status-alert"
    case Status.Cancelled:
      return "status-no-status"
    case Status.NoStatus || (showCompleteOrders && stage.Status !== Status.Completed):
      return "status-no-status"
    default:
      return "status-no-status"
  }
}

const isCollect = (statusDetail: StatusDetail | undefined): statusDetail is Collect => {
  return (
    statusDetail !== undefined &&
    "siteName" in statusDetail &&
    "siteAddress" in statusDetail &&
    "collectionDate" in statusDetail &&
    "collectionStatus" in statusDetail
  )
}

interface GridStatusInfoProps {
  value: OrderResultRecord
  showCompleteOrders: boolean
}

function GridStatusInfo({ value, showCompleteOrders }: GridStatusInfoProps) {
  const stages = value.Stages || []
  const { ScheduleStatusID, TestEventStatus, TestDetailClinicalID, IsExpired, ExpirationDate, ColStatus } = value

  const showEventStatus = ScheduleStatusID > 0 && TestDetailClinicalID && TestEventStatus === "Cancelled"
  const collect = stages.find((s) => s.Name === "Collect")?.StatusDetail
  const collectDetail = collect && isCollect(collect) ? collect.CollectionStatus : undefined
  // -eslint-disable-next-line no-nested-ternary, prettier/prettier
  const extraStatus = ScheduleStatusID === 11 // Canceled
      ? TestEventStatus
      : !!ColStatus && collectDetail !== "Collected"
        ? ColStatus
        : ""
  const shownStage = stages.find((stage) => stage.Status === Status.Completed) || stages[stages.length - 1]

  if (!stages.length) return null

  return (
    <>
      <Container className="d-none d-sm-block overflow-hidden" style={{ whiteSpace: "nowrap" }}>
        {shownStage?.IsStatusVisible ? (
          <Row>
            <Col className={`${stepStatusColor(shownStage, showCompleteOrders)} px-1 step`}>
              <span className="numbers mr-2">
                {showCompleteOrders && shownStage.Status !== 1 ? "X" : shownStage.Sequence}
              </span>
              <span className="montserrat-regular">
                {showEventStatus ? TestEventStatus : shownStage.StatusDetail.Description}
              </span>
              <span className="status-details">
                {ScheduleStatusID > 0 && !shownStage?.StatusDetail?.Detail
                  ? extraStatus
                  : shownStage?.StatusDetail?.Detail}
              </span>
              {IsExpired && !!ExpirationDate && (
                <OverlayTrigger
                  placement="top"
                  delay={{ show: 100, hide: 400 }}
                  overlay={
                    <Popover id="exp-date">
                      <Popover.Title as="h3">Expiration Date:</Popover.Title>
                      <Popover.Content>{FormatShortDateTime(ExpirationDate)}</Popover.Content>
                    </Popover>
                  }
                >
                  <img
                    src={ImageOrderExpired}
                    alt="Order expired"
                    width="20"
                    height="20"
                    className="ml-1"
                    aria-label="Order expired icon"
                  />
                </OverlayTrigger>
              )}
            </Col>
          </Row>
        ) : (
          <Row>
            <Col>{shownStage.Name}</Col>
          </Row>
        )}
      </Container>
      <Container className="d-block d-sm-none">
        {stages.slice(-2).map((stage) => (
          <div key={stage.Name.replace(/\s/g, "_")}>
            {stage.IsStatusVisible ? (
              <>
                <Row>
                  <Col>{stage.Name}</Col>
                </Row>
                <Row className="color-status">
                  <Col className={stepStatusColor(stage, showCompleteOrders)} />
                </Row>
              </>
            ) : (
              <Row>
                <Col>{stage.Name}</Col>
              </Row>
            )}
          </div>
        ))}
      </Container>
    </>
  )
}

export default GridStatusInfo
